import { Fragment, useEffect, useState } from "react";
import { FileText, ChevronRight, CircleCheck, Clock, Square, CircleAlert, RotateCw, Plug, History, FileLock2, Loader } from "lucide-react";
import { Inline } from "@/lib/inline";
import { EngineMark } from "@/components/ProviderLogo";
import { Tag, Label11 } from "@/components/common";
import { Button } from "@/components/ui/button";
import { docName, docFolderLine, versionDetail } from "@/lib/workKinds";
import { duration } from "@/components/terminal";

// The session workspace's documents view (design_handoff_documents_view/screens/Documents Workspace.dc.html): the same
// session and events as the terminal, drawn for people who don't code. Sans-serif messages, documents as cards, each
// turn that saved something ending with "Saved as version N"; no git words (they stay under Technical details).

// The tools a turn used, in plain words: "Read 3 documents · Changed 2 documents"
const TOOL_WORDS = {
  Read: ["Read", "document", "documents"], Edit: ["Changed", "document", "documents"], MultiEdit: ["Changed", "document", "documents"],
  Write: ["Wrote", "document", "documents"], NotebookEdit: ["Changed", "notebook", "notebooks"],
  Grep: ["Searched", "time", "times"], Glob: ["Searched", "time", "times"], Bash: ["Ran", "command", "commands"],
  WebSearch: ["Searched the web", "time", "times"], WebFetch: ["Read", "web page", "web pages"],
  Task: ["Asked a helper", "time", "times"], Agent: ["Asked a helper", "time", "times"], TodoWrite: ["Updated its plan", "time", "times"],
  MCP: ["Used a connected tool", "time", "times"],
};
export function docToolSummary(counts) {
  const parts = new Map();
  for (const [tool, n] of Object.entries(counts || {})) {
    const [verb, one, many] = TOOL_WORDS[tool] || ["Used a tool", "time", "times"];
    const key = `${verb}|${one}|${many}`;
    parts.set(key, (parts.get(key) || 0) + n);
  }
  return [...parts].map(([key, n]) => { const [verb, one, many] = key.split("|"); return `${verb} ${n} ${n === 1 ? one : many}`; }).join(" · ");
}

const STATE = { new: ["New", "info"], changed: ["Changed", "warning"], removed: ["Removed", "danger"] };
export function DocTag({ state, className }) {
  const [word, tone] = STATE[state] || STATE.changed;
  return <Tag tone={tone} className={className}>{word}</Tag>;
}

function Avatar({ engine }) {
  return (
    <span aria-hidden="true" className="w-6 h-6 rounded-full bg-gr-surface-2 grid place-items-center shrink-0">
      <EngineMark engine={engine} className="w-3 h-3" />
    </span>
  );
}

// The card at the top of the conversation: the company, the objective, where they work, the connected tools
export function WelcomeCard({ shortId, company, objective, folders, tools, pdfs }) {
  const rows = [["Company", company], ["Objective", objective || "General"],
    ["You work in", folders?.length ? folders.join(", ") : "All the project's files"],
    ...(tools?.length ? [["Connected tools", tools.join(", ")]] : [])];
  return (
    <section aria-label="About this session" className="bg-card rounded-[10px] px-5 py-[18px] flex flex-col gap-3" data-testid="documents-welcome">
      <div className="flex flex-col gap-0.5">
        <Label11>{[company, `Session ${shortId}`].filter(Boolean).join(" · ")}</Label11>
        <span className="font-display text-[15px] leading-[22px] tracking-[0.02em] font-semibold">{objective || "General"}</span>
      </div>
      <dl className="m-0 grid grid-cols-[max-content_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
        {rows.filter(([, v]) => v).map(([k, v]) => <Fragment key={k}><dt className="text-muted-foreground">{k}</dt><dd className="m-0 [overflow-wrap:anywhere]">{v}</dd></Fragment>)}
      </dl>
      <div className="flex flex-col gap-1.5 pt-3 border-t text-sm">
        <span className="flex gap-2 items-start"><History className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />Every change is saved as a version for you.</span>
        {pdfs && <span className="flex gap-2 items-start"><FileLock2 className="w-3.5 h-3.5 mt-0.5 shrink-0 text-muted-foreground" />The agent can read your PDFs too, without changing them.</span>}
      </div>
    </section>
  );
}

export function Notice({ children, testid }) {
  return (
    <div role="note" className="flex gap-2.5 px-3.5 py-2.5 border rounded-lg text-sm" data-testid={testid}>
      <Plug className="w-4 h-4 mt-px shrink-0 text-gr-warning" /><span className="text-gr-ink-secondary [text-wrap:pretty]">{children}</span>
    </div>
  );
}

// One changed document in the conversation; the card opens its changes
function DocCard({ path, state, onOpen, version }) {
  return (
    <button type="button" onClick={() => onOpen(path, version)} title="See changes" data-testid={`doc-card-${path}`}
      className="w-full flex items-center gap-3 px-3 py-2.5 border rounded-lg text-left hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring">
      <FileText className="w-[18px] h-[18px] shrink-0 text-muted-foreground" />
      <span className="flex-1 min-w-0 flex flex-col">
        <span className="font-semibold [overflow-wrap:anywhere]">{docName(path)}</span>
        <span className="text-sm text-muted-foreground [overflow-wrap:anywhere]">{docFolderLine(path)}</span>
      </span>
      <DocTag state={state} />
      <span className="flex items-center gap-0.5 text-sm font-semibold whitespace-nowrap">
        <span className="hidden sm:inline">See changes</span><ChevronRight className="w-3.5 h-3.5 rtl:-scale-x-100" />
      </span>
    </button>
  );
}

// The conversation. `versions`: the session's versions (versionsOf), to number a commit by its sha; `stateOf(path, n)`:
// a document's state in version n; `onOpen(path, n)` opens its changes; `onRetry()` sends the last message again.
export function DocTranscript({ entries, versions, stateOf, engine, onOpen, onRetry }) {
  const bySha = Object.fromEntries((versions || []).map((v) => [(v.sha || "").slice(0, 7), v]));
  const out = [];
  let files = [];
  let pendingVersion = (versions || []).length;  // a commit not in the list yet (it's refetched): the next number
  const versionOf = (e) => {
    const known = bySha[(e.sha || "").slice(0, 7)];
    if (known) return known;
    const n = Number(/· (\d+) files?/.exec(e.message || "")?.[1] || e.files || 0);
    return n > 0 ? { n: ++pendingVersion, files: n } : null;
  };
  // a turn's files come before its commit: the cards wait for it, to know their version
  const flushCards = (key, v) => {
    if (!files.length) return;
    const paths = [...new Set(files.map((f) => f.path))];
    out.push(
      <div key={`cards-${key}`} className="ps-[34px] flex flex-col gap-1.5">
        {paths.map((p) => <DocCard key={p} path={p} state={stateOf ? stateOf(p, v?.n) : "changed"} version={v?.n} onOpen={onOpen} />)}
      </div>,
    );
    files = [];
  };
  entries.forEach((e, i) => {
    if (e.kind === "file") { files.push(e); return; }
    if (e.kind === "commit") {
      const v = versionOf(e);
      flushCards(i, v);
      if (v) out.push(
        <div key={i} className="ps-[34px] flex flex-wrap items-center gap-x-1.5 gap-y-1 text-sm" data-testid={`doc-saved-${v.n}`}>
          <CircleCheck className="w-3.5 h-3.5 shrink-0 text-gr-success" /><span className="font-semibold">Saved as version {v.n}</span>
          <span className="text-muted-foreground">{versionDetail(v)}</span>
        </div>,
      );
      return;
    }
    flushCards(i, null);
    switch (e.kind) {
      case "user":
        out.push(
          <div key={i} className="flex justify-end pt-1.5" data-testid={`msg-user-${i}`}>
            <div className="max-w-[85%] bg-card rounded-[10px] px-3.5 py-2.5 flex flex-col gap-1.5">
              <span className="whitespace-pre-wrap [overflow-wrap:anywhere]">{e.text}</span>
              {e.images && (
                <span className="flex flex-wrap gap-2">
                  {e.images.map((img, j) => (img.url
                    ? <a key={j} href={img.url} target="_blank" rel="noreferrer"><img src={img.url} alt={`Screenshot ${j + 1}`} className="max-h-40 max-w-[240px] rounded border" /></a>
                    : <span key={j} className="h-16 w-24 rounded border bg-gr-surface-2" aria-label="Screenshot loading" />))}
                </span>
              )}
            </div>
          </div>,
        );
        break;
      case "assistant":
        out.push(
          <div key={i} className="flex gap-2.5 items-start" data-testid={`msg-assistant-${i}`}>
            <Avatar engine={engine} />
            <div className="pt-0.5 min-w-0 whitespace-pre-wrap [overflow-wrap:anywhere] text-gr-ink-secondary"><Inline text={e.text} /></div>
          </div>,
        );
        break;
      case "tools":
        out.push(<div key={i} className="ps-[34px] text-sm text-muted-foreground">{docToolSummary(e.counts)}</div>);
        break;
      case "done":
        out.push(<div key={i} className="ps-[34px] flex items-center gap-1.5 text-sm text-muted-foreground"><Clock className="w-3.5 h-3.5" />{e.text}</div>);
        break;
      case "stopped":
        out.push(<div key={i} className="ps-[34px] flex items-start gap-1.5 text-sm"><Square className="w-3.5 h-3.5 mt-0.5 shrink-0" />Stopped. What it changed so far is saved as a version.</div>);
        break;
      case "notice":
        out.push(<Notice key={i} testid="doc-notice">{e.server ? `${e.server} didn't start, so the agent works without it until you next unlock this session. Your founder can check how it's set up.` : e.text}</Notice>);
        break;
      case "error":
        out.push(
          <div key={i} role="alert" className="ps-[34px] flex flex-col items-start gap-2" data-testid={`msg-error-${i}`}>
            <span className="flex gap-2 text-gr-danger"><CircleAlert className="w-4 h-4 mt-0.5 shrink-0" /><span className="[text-wrap:pretty] [overflow-wrap:anywhere]">{e.text}</span></span>
            {onRetry && e.retry && <Button variant="outline" size="sm" onClick={onRetry}><RotateCw />Try again</Button>}
          </div>,
        );
        break;
      default:
        out.push(<div key={i} className="text-sm text-muted-foreground text-center whitespace-pre-wrap [text-wrap:pretty]">{e.text}</div>);
    }
  });
  flushCards("end", null);
  return out;
}

// The agent at work. Its Stop is the composer's, in Send's place (owner, 2026-10-13: one Stop button).
export function DocWorking({ since, tool, engine, stopping }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const doing = TOOL_WORDS[tool]?.[0];
  return (
    <div role="status" className="flex flex-wrap items-center gap-x-2.5 gap-y-2" data-testid="documents-working">
      <Avatar engine={engine} />
      <Loader className="w-3.5 h-3.5 text-gr-warning motion-safe:animate-spin [animation-duration:2s]" aria-hidden="true" />
      <span className="text-gr-ink-secondary">{stopping ? "Stopping…" : doing ? `Working · ${doing.toLowerCase()}…` : "Working…"}</span>
      <span className="text-sm text-muted-foreground tabular-nums">{duration(now - since)}</span>
    </div>
  );
}
